/**
 * Панель уведомлений (docs/06-notifications.md, docs/08-screens.md).
 *
 * Две вкладки: активные условия и история снятых. Каждое уведомление
 * ведёт на свой экран и объясняет, почему оно появилось и почему исчезло.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, Icon, Select, Tabs, Tag } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { Provenance } from '@/components/money';
import {
  useNotifications,
  useOperation,
  type NotificationGroup,
  type NotificationLevel,
  type NotificationRow,
} from '@/api/hooks';
import { formatDateTime, formatRelative, plural } from '@/lib/format';
import { useControlSize } from '@/lib/useControlSize';
import { QueryState } from '@/components/QueryState';

const LEVEL_TONE: Record<NotificationLevel, 'info' | 'warning' | 'danger'> = {
  info: 'info',
  warning: 'warning',
  critical: 'danger',
};

const LEVEL_LABEL: Record<NotificationLevel, string> = {
  info: 'к сведению',
  warning: 'внимание',
  critical: 'срочно',
};

const LEVEL_ICON: Record<NotificationLevel, string> = {
  info: 'Info',
  warning: 'AlertTriangle',
  critical: 'AlertOctagon',
};

const CHANNEL_LABEL: Record<string, string> = {
  telegram: 'Telegram',
  web_push: 'браузер',
};

const STATUS_LABEL: Record<string, string> = {
  pending: 'ждёт утра',
  sent: 'отправлено',
  failed: 'не доставлено',
  skipped: 'не отправлено',
};

function ChannelNote({ notification }: { notification: NotificationRow }) {
  if (notification.channels.length === 0) return null;

  // Причина показывается рядом со статусом: «не отправлено» без объяснения
  // выглядит поломкой, хотя чаще всего канал просто не подключён.
  const parts = notification.channels.map((item) => {
    const status = STATUS_LABEL[item.status] ?? item.status;
    const label = CHANNEL_LABEL[item.channel] ?? item.channel;
    return item.error ? `${label} — ${status}: ${item.error}` : `${label} — ${status}`;
  });

  return <Provenance>{parts.join(' · ')}</Provenance>;
}

function NotificationCard({
  notification,
  onRead,
  onDismiss,
  busy,
}: {
  notification: NotificationRow;
  onRead: (id: string) => void;
  onDismiss: (id: string) => void;
  busy: boolean;
}) {
  const unread = notification.readAt === null && notification.resolvedAt === null;

  return (
    <Card padding="md" tone={unread ? 'plain' : 'sunken'}>
      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <span
          style={{
            width: 32,
            height: 32,
            flex: '0 0 auto',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-brand-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={LEVEL_ICON[notification.level]} size={17} color="var(--burgundy-700)" />
        </span>

        <div style={{ flex: '1 1 auto', minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                font: '500 15px var(--font-display)',
                letterSpacing: 'var(--ls-heading)',
                color: 'var(--text-strong)',
              }}
            >
              {notification.title}
            </span>
            <Badge tone={LEVEL_TONE[notification.level]}>{LEVEL_LABEL[notification.level]}</Badge>
            {unread ? <Badge tone="brand" dot>новое</Badge> : null}
          </div>

          <p
            style={{
              marginTop: 'var(--space-2)',
              font: '400 14px/1.6 var(--font-text)',
              color: 'var(--text-muted)',
            }}
          >
            {notification.body}
          </p>

          <div style={{ marginTop: 'var(--space-3)' }}>
            <Provenance>
              {`${notification.groupTitle} · ${notification.typeTitle} · ${formatRelative(notification.createdAt)}`}
            </Provenance>
            {notification.resolvedAt ? (
              <Provenance>
                {`Снято ${formatDateTime(notification.resolvedAt)}` +
                  (notification.resolvedReason ? `: ${notification.resolvedReason}` : '')}
              </Provenance>
            ) : (
              <ChannelNote notification={notification} />
            )}
          </div>

          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
            }}
          >
            <Link to={notification.href} className="kj-link-plain">
              <Button variant="secondary" size="sm" iconRight={<Icon name="ArrowRight" size={15} />}>
                Открыть
              </Button>
            </Link>
            {unread ? (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRead(notification.id)}>
                Прочитано
              </Button>
            ) : null}
            {notification.resolvedAt === null ? (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onDismiss(notification.id)}>
                Убрать
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  );
}

export function NotificationsScreen() {
  const size = useControlSize();
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [group, setGroup] = useState<NotificationGroup | ''>('');
  const [level, setLevel] = useState<NotificationLevel | ''>('');
  const [unreadOnly, setUnreadOnly] = useState(false);

  const list = useNotifications({
    tab,
    ...(group ? { group } : {}),
    ...(level ? { level } : {}),
    unreadOnly: tab === 'active' ? unreadOnly : false,
  });

  const markRead = useOperation<{ id?: string }, { count: number }>('notification.mark_read');
  const dismiss = useOperation<{ id: string }>('notification.dismiss');
  const recheck = useOperation<Record<string, never>, { raised: number; resolved: number }>(
    'notification.recheck',
  );

  const notifications = list.data?.notifications ?? [];
  const unread = list.data?.unreadCount ?? 0;
  const busy = markRead.isPending || dismiss.isPending;

  return (
    <div className="kj-page">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div className="kj-tabs-touch">
          <Tabs
            items={[
              { value: 'active', label: 'Активные' },
              { value: 'history', label: 'История' },
            ]}
            value={tab}
            onChange={(value) => setTab(value as 'active' | 'history')}
          />
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Button
            variant="ghost"
            size={size}
            loading={recheck.isPending}
            iconLeft={<Icon name="RefreshCw" size={15} />}
            onClick={() => recheck.mutate({} as Record<string, never>)}
          >
            Проверить сейчас
          </Button>
          {tab === 'active' && unread > 0 ? (
            <Button variant="secondary" size={size} disabled={busy} onClick={() => markRead.mutate({})}>
              Прочитать все
            </Button>
          ) : null}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
        }}
      >
        <Select
          size={size}
          options={[
            { value: '', label: 'Все разделы' },
            { value: 'money', label: 'Деньги' },
            { value: 'tasks', label: 'Задачи' },
            { value: 'system', label: 'Система' },
          ]}
          value={group}
          onChange={(e) => setGroup(e.target.value as NotificationGroup | '')}
        />
        <Select
          size={size}
          options={[
            { value: '', label: 'Любой уровень' },
            { value: 'critical', label: 'Срочные' },
            { value: 'warning', label: 'Внимание' },
            { value: 'info', label: 'К сведению' },
          ]}
          value={level}
          onChange={(e) => setLevel(e.target.value as NotificationLevel | '')}
        />
        {tab === 'active' ? (
          <Tag selected={unreadOnly} onClick={() => setUnreadOnly((value) => !value)}>
            Только непрочитанные
          </Tag>
        ) : null}
      </div>

      {list.isLoading || list.isError ? (
        <QueryState query={list} what="уведомления" />
      ) : notifications.length === 0 ? (
        tab === 'active' ? (
          <EmptyState
            icon="Bell"
            title={
              group || level || unreadOnly
                ? 'Под фильтр ничего не подходит'
                : 'Активных уведомлений нет'
            }
            description="Мы пишем, когда нужно действие: баланс подходит к концу, расход резко вырос, задача просрочена. Пока всё в порядке, здесь тихо."
          />
        ) : (
          <EmptyState
            icon="Archive"
            title="История пуста"
            description="Сюда попадают снятые уведомления с пометкой, когда и почему условие перестало выполняться. Например, «баланс пополнен 3 сентября»."
          />
        )
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {tab === 'active' ? (
            <Provenance>
              {`${notifications.length} ${plural(notifications.length, 'условие держится', 'условия держатся', 'условий держится')}. ` +
                'Уведомление снимается само, когда условие перестаёт выполняться.'}
            </Provenance>
          ) : null}

          {notifications.map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              busy={busy}
              onRead={(id) => markRead.mutate({ id })}
              onDismiss={(id) => dismiss.mutate({ id })}
            />
          ))}
        </div>
      )}
    </div>
  );
}
